import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DashboardPageWrapper,
  Toolbar,
  Title,
  ContentArea,
  MainContent,
  ChartsGrid,
  ChartContainer,
  ChartTitle,
  FiltersWrapper,
} from "./styles";
import { KpiSection } from "./sections";
import api from "../../network/api";
import { Statistics2, SubscriptionCustomer } from "../../types/Statistics2";
import { CircularProgress, Menu, MenuItem } from "@mui/material";
import { LoadingText } from "../../components/data/styles";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import colors from "../../styles/colors";

const CUSTOMER_COLORS = [
  colors.theme.galilBlue,
  colors.theme.galilGreen,
  colors.theme.galilOrange,
  "#9C27B0",
  "#E91E63",
  "#009688",
  "#3F51B5",
  "#FF5722",
  "#795548",
  "#607D8B",
];

const selectStyle: React.CSSProperties = {
  background: "transparent",
  color: "white",
  border: "1px solid rgba(255,255,255,0.4)",
  borderRadius: 6,
  padding: "4px 8px",
  fontSize: "0.85rem",
  cursor: "pointer",
};

interface CustomerDropdownProps {
  customers: SubscriptionCustomer[];
  selectedId: string;
  onSelect: (id: string) => void;
  allLabel: string;
}

const CustomerDropdown: React.FC<CustomerDropdownProps> = ({
  customers,
  selectedId,
  onSelect,
  allLabel,
}) => {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const selectedCustomer = customers.find((c) => c.customerId === selectedId);
  const selectedColor = selectedCustomer
    ? CUSTOMER_COLORS[
        customers.indexOf(selectedCustomer) % CUSTOMER_COLORS.length
      ]
    : null;

  return (
    <>
      <button
        onClick={(e) => setAnchor(e.currentTarget)}
        style={{
          ...selectStyle,
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "1px solid rgba(255,255,255,0.4)",
        }}
      >
        {selectedColor && (
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: selectedColor,
              flexShrink: 0,
            }}
          />
        )}
        {selectedCustomer?.customerName ?? allLabel}
        <span style={{ fontSize: "0.7rem", opacity: 0.7, marginLeft: 2 }}>
          ▾
        </span>
      </button>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ style: { minWidth: 180 } }}
      >
        <MenuItem
          selected={selectedId === ""}
          onClick={() => { onSelect(""); setAnchor(null); }}
        >
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "rgba(0,0,0,0.2)",
              marginInlineEnd: 10,
              flexShrink: 0,
            }}
          />
          {allLabel}
        </MenuItem>
        {customers.map((c, i) => {
          const color = CUSTOMER_COLORS[i % CUSTOMER_COLORS.length];
          return (
            <MenuItem
              key={c.customerId}
              selected={selectedId === c.customerId}
              onClick={() => { onSelect(c.customerId); setAnchor(null); }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: color,
                  marginInlineEnd: 10,
                  flexShrink: 0,
                }}
              />
              <span style={{ color }}>{c.customerName}</span>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

const StatisticsPage: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [stats, setStats] = useState<Statistics2 | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [allCustomers, setAllCustomers] = useState<SubscriptionCustomer[]>([]);

  const monthOptions = useMemo(() => {
    return Array.from({ length: 3 }, (_, i) => {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - i);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString(i18n.language, {
        month: "long",
        year: "numeric",
      });
      return { value, label };
    });
  }, [i18n.language]);

  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      const data = await api.getStatistics2({
        month: selectedMonth,
        ...(selectedCustomerId ? { customer_id: selectedCustomerId } : {}),
      });
      setStats(data);
      // Preserve the full customer list — only update it on the aggregate load
      // so the dropdown doesn't lose options after filtering by a customer.
      if (!selectedCustomerId && data?.subscriptionStats?.customers?.length) {
        setAllCustomers(data.subscriptionStats.customers);
      }
      setLoading(false);
    };
    fetchStats();
  }, [selectedMonth, selectedCustomerId]);

  if (loading) {
    return (
      <DashboardPageWrapper>
        <Toolbar>
          <Title style={{ margin: "0 auto" }}>
            {t("ADVANCED_TRANSACTION_ANALYTICS")}
          </Title>
        </Toolbar>
        <ContentArea
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
          <LoadingText>{t("LOADING_DATA")}</LoadingText>
        </ContentArea>
      </DashboardPageWrapper>
    );
  }

  if (!stats) return null;

  return (
    <DashboardPageWrapper>
      <Toolbar>
        <FiltersWrapper>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={selectStyle}
          >
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value} style={{ color: "black" }}>
                {m.label}
              </option>
            ))}
          </select>
          {allCustomers.length > 0 && (
            <CustomerDropdown
              customers={allCustomers}
              selectedId={selectedCustomerId}
              onSelect={setSelectedCustomerId}
              allLabel={t("ALL_CUSTOMERS") || "All Customers"}
            />
          )}
        </FiltersWrapper>
        <Title>{t("ADVANCED_TRANSACTION_ANALYTICS")}</Title>
      </Toolbar>

      <ContentArea>
        <MainContent>
          <KpiSection
            overview={stats.overview}
            subscriptionStats={stats.subscriptionStats}
            pagesByDay={stats.pagesByDay}
          />

          <ChartsGrid>
            <ChartContainer>
              <ChartTitle>{t("PAGES_PER_DAY")}</ChartTitle>
              {stats.pagesByDay.length === 0 ? (
                <div
                  style={{
                    height: 250,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.theme.galilGrey,
                    fontSize: "0.9rem",
                  }}
                >
                  {t("NO_DATA_FOR_MONTH")}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={stats.pagesByDay.map((d) => ({
                      ...d,
                      not_success:
                        (d.failed || 0) +
                        (d.exception || 0) +
                        (d.calc_error || 0),
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="success"
                      stackId="pages"
                      fill={colors.theme.galilGreen}
                      name={t("PAGES_SUCCESS")}
                    />
                    <Bar
                      dataKey="not_success"
                      stackId="pages"
                      fill={colors.theme.galilOrange}
                      name={t("PAGES_FAILED")}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartContainer>
          </ChartsGrid>
        </MainContent>
      </ContentArea>
    </DashboardPageWrapper>
  );
};

export default StatisticsPage;
